import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../../../HandleAPICalls/actions";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Import Modal components
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import UpdateCorporateComponent from "./component";

const CorporateUpdateContainer = (props) => {
  const { corporateId } = useParams();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fetchingDepartments, setFetchingDepartments] = useState(false);
  const [fetchingCorporateDetails, setFetchingCorporateDetails] = useState(false);
  
  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);

  // Corporate data
  const [createCorporateData, setCreateCorporateData] = useState({
    name: "",
    domains: []
  });

  // Department data
  const [useDefaultDepartment, setUseDefaultDepartment] = useState(true);
  const [defaultBalance, setDefaultBalance] = useState("");
  const [departments, setDepartments] = useState([
    { departmentId: "", initialBalance: "", name: "" }
  ]);
  const [availableDepartments, setAvailableDepartments] = useState([]);
  const [initialDepartments, setInitialDepartments] = useState([]);
  const [initialDefaultBalance, setInitialDefaultBalance] = useState("");

  // Usage limit data
  const [usageLimitRequired, setUsageLimitRequired] = useState("No");
  const [usageLimitData, setUsageLimitData] = useState({
    limitOn: "",
    frequency: "",
    value: ""
  });

  const navigate = useNavigate();
  const isUpdateMode = !!corporateId;

  // Fetch available departments from API
  useEffect(() => {
    handleGetDepartmentsList();
    if (corporateId) {
      handleGetCorporateDetails(corporateId);
    }
  }, [corporateId]);

  const handleGetDepartmentsList = () => {
    setFetchingDepartments(true);
    props.getDataFromAPI(
      `/partner/api/v2/corporate/all/departments`,
      "GET",
      undefined,
      (response) => {
        setFetchingDepartments(false);
        if (response && Array.isArray(response)) {
          const formattedDepartments = response.map(dept => ({
            id: dept.departmentId || dept.id || dept.department_id,
            name: dept.departmentName || dept.name || dept.department_name
          })).filter(dept => dept.id && dept.name);
          
          setAvailableDepartments(formattedDepartments);
        } else {
          setAvailableDepartments([]);
        }
      },
      (err) => {
        setFetchingDepartments(false);
        console.error("Failed to fetch departments:", err);
        setAvailableDepartments([]);
      }
    );
  };

  const handleGetCorporateDetails = (corporateId) => {
    setFetchingCorporateDetails(true);
    props.getDataFromAPI(
      `/partner/api/v1/corp/${corporateId}/details`,
      "GET",
      undefined,
      (response) => {
        setFetchingCorporateDetails(false);
        
        if (response && response) {
          const data = response;
          
          // Set corporate name
          if (data.corporateName) {
            setCreateCorporateData(prev => ({
              ...prev,
              name: data.corporateName
            }));
          }
          
          // Set domains
          if (data.corporateDomains && Array.isArray(data.corporateDomains)) {
            const formattedDomains = data.corporateDomains.map(domain => ({
              domain: domain
            }));
            setCreateCorporateData(prev => ({
              ...prev,
              domains: formattedDomains
            }));
          }
          
          // Set department configuration based on API response
          if (data.departments && Array.isArray(data.departments) && data.departments.length > 0) {
            const defaultDept = data.departments.find(dept => 
              dept.departmentId === 0 || dept.departmentName === "Default"
            );
            
            if (defaultDept) {
              setUseDefaultDepartment(true);
              const balanceValue = defaultDept.initialBalance !== undefined && defaultDept.initialBalance !== null 
                ? defaultDept.initialBalance.toString() 
                : "";
              setDefaultBalance(balanceValue);
              setInitialDefaultBalance(balanceValue);
              setDepartments([{ departmentId: "", initialBalance: "", name: "" }]);
            } else {
              setUseDefaultDepartment(false);
              const formattedDepartments = data.departments.map(dept => ({
                departmentId: dept.departmentId,
                initialBalance: dept.initialBalance !== undefined ? dept.initialBalance.toString() : "0",
                name: dept.departmentName || `Department ${dept.departmentId}`
              }));
              setDepartments(formattedDepartments);
              setInitialDepartments(JSON.parse(JSON.stringify(formattedDepartments)));
              setDefaultBalance("");
            }
          } else {
            setUseDefaultDepartment(true);
            setDefaultBalance("");
            setInitialDefaultBalance("");
            setDepartments([{ departmentId: "", initialBalance: "", name: "" }]);
          }
          
          // FIXED: Handle usage limit correctly
          if (data.usageLimit) {
            const usageLimit = data.usageLimit;
            
            // Check if usage limit is required and has valid values
            if (usageLimit.usageLimitRequired === true) {
              // Check if limitOn and frequency have valid values
              if (usageLimit.limitOn && usageLimit.frequency && usageLimit.value !== undefined && usageLimit.value !== null && usageLimit.value > 0) {
                // Fully configured usage limit
                setUsageLimitRequired("Yes");
                
                // Convert frequency from API format (HOUR/DAY/MONTH) to UI format (HOURS/DAYS/MONTH)
                let uiFrequency = usageLimit.frequency;
                if (uiFrequency === "HOUR") uiFrequency = "HOURS";
                if (uiFrequency === "DAY") uiFrequency = "DAYS";
                if (uiFrequency === "MONTH") uiFrequency = "MONTH";
                
                setUsageLimitData({
                  limitOn: usageLimit.limitOn || "",
                  frequency: uiFrequency,
                  value: usageLimit.value.toString()
                });
              } else {
                // usageLimitRequired is true but limitOn/frequency are null/empty
                // This means the corporate has usage limit enabled but not configured
                // Set to "Yes" but with empty data to allow configuration
                setUsageLimitRequired("Yes");
                setUsageLimitData({
                  limitOn: "",
                  frequency: "",
                  value: ""
                });
              }
            } else {
              // usageLimitRequired is false
              setUsageLimitRequired("No");
              setUsageLimitData({
                limitOn: "",
                frequency: "",
                value: ""
              });
            }
          } else {
            // No usage limit object in response
            setUsageLimitRequired("No");
            setUsageLimitData({
              limitOn: "",
              frequency: "",
              value: ""
            });
          }
        }
      },
      (err) => {
        setFetchingCorporateDetails(false);
        console.error("Failed to fetch corporate details:", err);
        toast.error("Failed to fetch corporate details");
      }
    );
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (!createCorporateData.name.trim()) {
        toast.error("Please enter business name");
        return;
      }

      const hasValidDomains = createCorporateData.domains && 
        createCorporateData.domains.some(domain => domain.domain && domain.domain.trim());
      
      if (!hasValidDomains) {
        toast.error("Please enter at least one domain");
        return;
      }
      
      setActiveStep((prevStep) => prevStep + 1);
      
    } else if (activeStep === 1) {
      if (useDefaultDepartment) {
        if (!defaultBalance || parseFloat(defaultBalance) <= 0) {
          toast.error("Please enter a valid default balance");
          return;
        }
      } else {
        if (!departments || departments.length === 0) {
          toast.error("Please add at least one department");
          return;
        }
        
        for (let dept of departments) {
          if (!dept.departmentId || dept.departmentId === "") {
            toast.error("Please select a valid department from dropdown");
            return;
          }
          if (!dept.initialBalance || parseFloat(dept.initialBalance) <= 0) {
            toast.error("Please enter a valid initial balance for all departments");
            return;
          }
        }
      }
      
      setActiveStep((prevStep) => prevStep + 1);
      
    } else if (activeStep === 2) {
      if (usageLimitRequired === "Yes") {
        if (!usageLimitData.limitOn) {
          toast.error("Please select what you want to limit");
          return;
        }
        if (!usageLimitData.frequency) {
          toast.error("Please select frequency");
          return;
        }
        if (!usageLimitData.value || parseFloat(usageLimitData.value) <= 0) {
          toast.error("Please enter a valid limit value");
          return;
        }
      }
      
      handleFinalSubmit();
    }
  };

  const handleClose = () => {
    if (activeStep === 0) {
      navigate(-1);
    } else {
      setActiveStep((prevStep) => prevStep - 1);
    }
  };

  const handleChangeCorporate = (e) => {
    const { name, value } = e.target;
    
    if (name === "domain") {
      if (!value.trim()) return;
      
      const domainExists = createCorporateData.domains.some(
        domain => domain.domain.toLowerCase() === value.toLowerCase()
      );
      
      if (!domainExists) {
        setCreateCorporateData({
          ...createCorporateData,
          domains: [
            ...createCorporateData.domains,
            { domain: value.trim() }
          ]
        });
      } else {
        toast.warning("Domain already added");
      }
    } else {
      setCreateCorporateData({
        ...createCorporateData,
        [name]: value,
      });
    }
  };

  const handleRemoveDomain = (index) => {
    const newDomains = [...createCorporateData.domains];
    newDomains.splice(index, 1);
    setCreateCorporateData({
      ...createCorporateData,
      domains: newDomains
    });
  };

  const handleUseDefaultChange = (e) => {
    const isChecked = e.target.checked;
    setUseDefaultDepartment(isChecked);
    
    if (isChecked) {
      setDefaultBalance(initialDefaultBalance);
      setDepartments([{ departmentId: "", initialBalance: "", name: "" }]);
    } else {
      if (initialDepartments.length > 0) {
        setDepartments(JSON.parse(JSON.stringify(initialDepartments)));
      } else {
        setDepartments([{ departmentId: "", initialBalance: "", name: "" }]);
      }
      setDefaultBalance("");
    }
  };

  const handleDefaultBalanceChange = (value) => {
    const regex = /^\d*\.?\d*$/;
    if (regex.test(value) || value === "") {
      setDefaultBalance(value);
    }
  };

  const handleAddDepartment = () => {
    setDepartments([
      ...departments,
      { departmentId: "", initialBalance: "", name: "" }
    ]);
  };

  const handleRemoveDepartment = (index) => {
    if (departments.length > 1) {
      const newDepartments = [...departments];
      newDepartments.splice(index, 1);
      setDepartments(newDepartments);
    }
  };

  const handleDepartmentChange = (index, field, value) => {
    const newDepartments = [...departments];
    
    if (field === 'name') {
      const selectedDept = availableDepartments.find(dept => dept.name === value);
      if (selectedDept) {
        newDepartments[index].departmentId = selectedDept.id;
        newDepartments[index].name = selectedDept.name;
      } else {
        newDepartments[index].departmentId = "";
        newDepartments[index].name = value;
      }
    } else {
      newDepartments[index][field] = field === 'initialBalance' 
        ? (value || "") 
        : value;
    }
    
    setDepartments(newDepartments);
  };

  const handleBalanceChange = (index, value) => {
    const regex = /^\d*\.?\d*$/;
    if (regex.test(value) || value === "") {
      const newDepartments = [...departments];
      newDepartments[index].initialBalance = value;
      setDepartments(newDepartments);
    }
  };

  const handleUsageLimitChange = (value) => {
    setUsageLimitRequired(value);
    
    if (value === "No") {
      setUsageLimitData({
        limitOn: "",
        frequency: "",
        value: ""
      });
    }
  };

  const handleUsageLimitDataChange = (field, value) => {
    setUsageLimitData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleUsageLimitValueChange = (e) => {
    const value = e.target.value;
    
    if (value === "") {
      setUsageLimitData(prev => ({
        ...prev,
        value: ""
      }));
      return;
    }
    
    if (usageLimitData.limitOn === "AMOUNT") {
      if (/^\d*\.?\d{0,2}$/.test(value) && value.length <= 20) {
        setUsageLimitData(prev => ({
          ...prev,
          value: value
        }));
      }
    } else if (usageLimitData.limitOn === "TRANSACTION") {
      if (/^\d*$/.test(value) && value.length <= 20) {
        setUsageLimitData(prev => ({
          ...prev,
          value: value
        }));
      }
    } else {
      if (/^\d*\.?\d*$/.test(value) && value.length <= 20) {
        setUsageLimitData(prev => ({
          ...prev,
          value: value
        }));
      }
    }
  };

  const handleClearUsageLimitValue = () => {
    setUsageLimitData(prev => ({
      ...prev,
      value: ""
    }));
  };

  const handleCopyCorporateId = () => {
    if (corporateId) {
      navigator.clipboard.writeText(corporateId.toString())
        .then(() => {
          setCopySuccess(true);
          toast.success("Corporate ID copied to clipboard!");
          setTimeout(() => {
            setCopySuccess(false);
          }, 3000);
        })
        .catch(err => {
          console.error('Failed to copy: ', err);
          toast.error("Failed to copy Corporate ID");
        });
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate(`/home/customermanager/corporatedashboard/${corporateId}`);
  };

  const handleFinalSubmit = () => {
    setLoading(true);
    
    const mainPayload = {
      name: createCorporateData.name.trim(),
      domains: createCorporateData.domains
        .filter(domain => domain.domain && domain.domain.trim())
        .map(domain => ({
          domain: domain.domain.trim()
        })),
      useDefaultDepartment: useDefaultDepartment
    };

    // Add department data based on checkbox state
    if (useDefaultDepartment) {
      if (defaultBalance) {
        mainPayload.defaultBalance = parseInt(defaultBalance) || 0;
      }
      mainPayload.departments = [];
    } else {
      const validDepartments = departments
        .filter(dept => dept.departmentId && dept.initialBalance)
        .map(dept => ({
          departmentId: parseInt(dept.departmentId),
          initialBalance: parseInt(dept.initialBalance) || 0
        }));
      
      if (validDepartments.length > 0) {
        mainPayload.departments = validDepartments;
      }
    }

    // Handle usage limit correctly
    if (usageLimitRequired === "Yes") {
      if (usageLimitData.value && usageLimitData.limitOn && usageLimitData.frequency) {
        // Fully configured usage limit
        mainPayload.usageLimit = {
          usageLimitRequired: true,
          limitOn: usageLimitData.limitOn,
          frequency: usageLimitData.frequency, // Already in HOURS/DAYS/MONTH format
          value: usageLimitData.limitOn === "AMOUNT" 
            ? parseFloat(usageLimitData.value) || 0 
            : parseInt(usageLimitData.value) || 0
        };
      } else {
        // usageLimitRequired is Yes but not fully configured
        // Send only the flag without limit details
        mainPayload.usageLimit = {
          usageLimitRequired: true
        };
      }
    } else {
      // If user selected No, send usageLimitRequired: false
      mainPayload.usageLimit = {
        usageLimitRequired: false
      };
    }

    console.log("Final Payload:", JSON.stringify(mainPayload, null, 2));

    const apiUrl = `/partner/api/v2/corporate/update/corp/${corporateId}`;
    const method = "PUT";

    props.getDataFromAPI(
      apiUrl,
      method,
      mainPayload,
      (response) => {
        setLoading(false);
        const actionType = isUpdateMode ? "updated" : "created";
        setSuccessMessage(`Corporate "${createCorporateData.name.trim()}" ${actionType} successfully!`);
        setShowSuccessModal(true);
        toast.success(`Corporate ${actionType} successfully!`);
      },
      (err) => {
        setLoading(false);
        console.error(`Failed to update corporate:`, err);
        
        let errorMessage = "Failed to update corporate";
        if (err.message) {
          errorMessage = err.message;
        } else if (err.response && err.response.data && err.response.data.message) {
          errorMessage = err.response.data.message;
        }
        
        toast.error(errorMessage);
      }
    );
  };

  return (
    <>
      <ToastContainer />
      <UpdateCorporateComponent
        activeStep={activeStep}
        handleNext={handleNext}
        handleClose={handleClose}
        createCorporateData={createCorporateData}
        handleChangeCorporate={handleChangeCorporate}
        setCreateCorporateData={setCreateCorporateData}
        handleRemoveDomain={handleRemoveDomain}
        departments={departments}
        handleAddDepartment={handleAddDepartment}
        handleRemoveDepartment={handleRemoveDepartment}
        handleDepartmentChange={handleDepartmentChange}
        handleBalanceChange={handleBalanceChange}
        useDefaultDepartment={useDefaultDepartment}
        handleUseDefaultChange={handleUseDefaultChange}
        defaultBalance={defaultBalance}
        handleDefaultBalanceChange={handleDefaultBalanceChange}
        availableDepartments={availableDepartments}
        fetchingDepartments={fetchingDepartments}
        usageLimitData={usageLimitData}
        handleUsageLimitDataChange={handleUsageLimitDataChange}
        handleUsageLimitValueChange={handleUsageLimitValueChange}
        handleClearUsageLimitValue={handleClearUsageLimitValue}
        usageLimitRequired={usageLimitRequired}
        handleUsageLimitChange={handleUsageLimitChange}
        loading={loading}
        isUpdateMode={isUpdateMode}
        fetchingCorporateDetails={fetchingCorporateDetails}
      />

      {/* Success Modal */}
      <Dialog
        open={showSuccessModal}
        onClose={handleSuccessModalClose}
        aria-labelledby="success-dialog-title"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            padding: '20px',
            maxWidth: '550px',
            width: '90%'
          }
        }}
      >
        <DialogTitle 
          id="success-dialog-title"
          sx={{ 
            textAlign: 'center',
            paddingTop: '40px',
            paddingBottom: '10px'
          }}
        >
          <CheckCircleIcon 
            sx={{ 
              fontSize: 80, 
              color: '#14AE78',
              marginBottom: '16px'
            }} 
          />
          <div style={{ fontSize: '24px', fontWeight: 600, color: '#333' }}>
            Success!
          </div>
        </DialogTitle>
        <DialogContent>
          <DialogContentText 
            sx={{ 
              textAlign: 'center',
              fontSize: '16px',
              color: '#666',
              lineHeight: '1.6',
              marginBottom: '16px'
            }}
          >
            {successMessage}
          </DialogContentText>
          
          {corporateId && (
            <div style={{ 
              backgroundColor: '#f8f9fa', 
              borderRadius: '10px', 
              padding: '16px', 
              margin: '20px 0',
              border: '1px solid #e8e8e8'
            }}>
              <div style={{ 
                fontSize: '14px', 
                color: '#666', 
                marginBottom: '8px',
                fontWeight: 500
              }}>
                Corporate ID:
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '12px',
                border: '1px solid #ddd'
              }}>
                <div style={{ 
                  fontSize: '16px', 
                  fontFamily: 'monospace',
                  fontWeight: '600',
                  color: '#333',
                  wordBreak: 'break-all'
                }}>
                  {corporateId}
                </div>
                <Button
                  onClick={handleCopyCorporateId}
                  startIcon={<ContentCopyIcon />}
                  size="small"
                  variant="outlined"
                  sx={{
                    marginLeft: '12px',
                    flexShrink: 0,
                    borderColor: '#14AE78',
                    color: '#14AE78',
                    '&:hover': {
                      borderColor: '#0f8c65',
                      backgroundColor: 'rgba(20, 174, 120, 0.05)'
                    }
                  }}
                >
                  {copySuccess ? 'Copied!' : 'Copy'}
                </Button>
              </div>
              <div style={{ 
                fontSize: '13px', 
                color: '#999', 
                marginTop: '8px',
                fontStyle: 'italic'
              }}>
                Save this ID for future reference
              </div>
            </div>
          )}
          
          <DialogContentText 
            sx={{ 
              textAlign: 'center',
              fontSize: '14px',
              color: '#999',
              marginTop: '16px'
            }}
          >
            You will be redirected to the corporate dashboard.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', padding: '24px' }}>
          <Button
            onClick={handleSuccessModalClose}
            variant="contained"
            sx={{
              backgroundColor: '#14AE78',
              color: 'white',
              borderRadius: '10px',
              padding: '12px 48px',
              fontSize: '15px',
              fontWeight: 500,
              textTransform: 'none',
              minWidth: '180px',
              '&:hover': {
                backgroundColor: '#0f8c65',
                boxShadow: '0 4px 12px rgba(20, 174, 120, 0.3)'
              }
            }}
          >
            Go to Corporate Dashboard
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

function mapStateToProps(state) {
  return {
    state,
  };
}

export default connect(mapStateToProps, {
  getDataFromAPI,
})(CorporateUpdateContainer);